/**
 * Zustand store slice — review tab state.
 *
 * Lifecycle:
 *   idle → loading_checklists → ready
 *   ready → extracting → evaluating → done | error
 */

import { create } from 'zustand';
import type {
  ChecklistOut,
  ReviewResultOut,
  ReviewSummary,
  RuleOut,
  SectionMatchType,
} from '../types';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type ReviewPhase =
  | 'idle'               // initial state, triggers checklist load
  | 'loading_checklists' // fetching checklist list from API
  | 'ready'              // checklist list loaded, waiting for user
  | 'extracting'         // reading Word document content
  | 'evaluating'         // waiting for evaluation job to complete
  | 'done'               // results available
  | 'error';             // unrecoverable error

export type RuleEvaluationStatus = 'pending' | 'done';

export interface RuleReviewItem {
  rule_id: string;
  rule_title: string;
  section_match: string | null;
  section_match_type: SectionMatchType;
  evaluationStatus: RuleEvaluationStatus;
  result: ReviewResultOut | null;
}

interface ReviewState {
  phase: ReviewPhase;
  checklists: ChecklistOut[];
  selectedChecklistId: string | null;
  evaluationJobId: string | null;
  ruleItems: RuleReviewItem[];
  globalError: string | null;

  setPhase: (phase: ReviewPhase) => void;
  setChecklists: (checklists: ChecklistOut[]) => void;
  removeChecklist: (id: string) => void;
  setSelectedChecklistId: (id: string | null) => void;
  setEvaluationJobId: (id: string | null) => void;
  initRuleItems: (rules: RuleOut[], unmatchedRuleIds: Set<string>) => void;
  mergeEvaluationResults: (results: ReviewResultOut[]) => void;
  finalizeRuleItems: (
    results: ReviewResultOut[],
    unmatchedRuleIds: Set<string>,
    rules: RuleOut[],
  ) => void;
  setGlobalError: (error: string | null) => void;
  reset: () => void;
  summary: () => ReviewSummary;
}

function buildUnmatchedResult(rule: RuleOut): ReviewResultOut {
  return {
    rule_id: rule.id,
    rule_title: rule.title,
    rule_condition: rule.condition,
    rule_criteria: rule.criteria,
    section_match: rule.section_match,
    section_match_type: rule.section_match_type,
    condition_met: null,
    verdict: 'na',
    reasoning: `No content control matching "${rule.section_match ?? ''}" was found in the document.`,
    evidence: [],
  };
}

function summaryFromResults(results: ReviewResultOut[]): ReviewSummary {
  return {
    total: results.length,
    pass_count: results.filter((r) => r.verdict === 'pass').length,
    fail_count: results.filter((r) => r.verdict === 'fail').length,
    na_count: results.filter((r) => r.verdict === 'na').length,
  };
}

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

export const useReviewStore = create<ReviewState>((set, get) => ({
  phase: 'idle',
  checklists: [],
  selectedChecklistId: null,
  evaluationJobId: null,
  ruleItems: [],
  globalError: null,

  setPhase: (phase) => set({ phase }),
  setChecklists: (checklists) => set({ checklists }),
  removeChecklist: (id) =>
    set((state) => ({
      checklists: state.checklists.filter((cl) => cl.id !== id),
      selectedChecklistId:
        state.selectedChecklistId === id ? null : state.selectedChecklistId,
    })),
  setSelectedChecklistId: (id) => set({ selectedChecklistId: id }),
  setEvaluationJobId: (id) => set({ evaluationJobId: id }),

  initRuleItems: (rules, unmatchedRuleIds) =>
    set({
      ruleItems: rules.map((rule) => {
        if (unmatchedRuleIds.has(rule.id)) {
          return {
            rule_id: rule.id,
            rule_title: rule.title,
            section_match: rule.section_match,
            section_match_type: rule.section_match_type,
            evaluationStatus: 'done',
            result: buildUnmatchedResult(rule),
          };
        }
        return {
          rule_id: rule.id,
          rule_title: rule.title,
          section_match: rule.section_match,
          section_match_type: rule.section_match_type,
          evaluationStatus: 'pending',
          result: null,
        };
      }),
    }),

  mergeEvaluationResults: (results) =>
    set((state) => {
      const byRuleId = new Map(results.map((r) => [r.rule_id, r]));
      return {
        ruleItems: state.ruleItems.map((item) => {
          const result = byRuleId.get(item.rule_id);
          if (!result || item.evaluationStatus === 'done') {
            return item;
          }
          return {
            ...item,
            evaluationStatus: 'done',
            result,
          };
        }),
      };
    }),

  finalizeRuleItems: (results, unmatchedRuleIds, rules) =>
    set((state) => {
      const byRuleId = new Map(results.map((r) => [r.rule_id, r]));
      const rulesById = new Map(rules.map((r) => [r.id, r]));
      const ruleItems = state.ruleItems.map((item) => {
        if (unmatchedRuleIds.has(item.rule_id)) {
          const rule = rulesById.get(item.rule_id);
          return {
            ...item,
            evaluationStatus: 'done' as const,
            result: rule ? buildUnmatchedResult(rule) : item.result,
          };
        }
        const result = byRuleId.get(item.rule_id);
        if (result) {
          return {
            ...item,
            evaluationStatus: 'done' as const,
            result,
          };
        }
        return item;
      });
      return { ruleItems };
    }),

  setGlobalError: (error) => set({ globalError: error }),

  reset: () =>
    set({
      phase: 'idle',
      checklists: [],
      selectedChecklistId: null,
      evaluationJobId: null,
      ruleItems: [],
      globalError: null,
    }),

  summary: () => {
    const doneResults = get()
      .ruleItems.map((item) => item.result)
      .filter((result): result is ReviewResultOut => result !== null);
    return summaryFromResults(doneResults);
  },
}));
